import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle'; // Import the activate icon
import VisibilityIcon from '@mui/icons-material/Visibility'; // Import the visibility icon
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { saveData, getData, openDB } from '../../../utils/db';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import BillPrint from '../Receipt/Bill';
import PrintIcon from '@mui/icons-material/Print';
import ReactDOM from 'react-dom';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

function ReceiptIndex() {
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null); // Track the item ID to delete
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const { isAuthenticated, companyId, userId, permissions, logout, token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false); //for search
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        // Token is not expired, fetch data
        fetchTableData();
    }, [isAuthenticated, companyId, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };


    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const hasCreateTransactionPermission = permissions.includes('Create Transaction');
    const hasEditTransactionPermission = permissions.includes('Edit Transaction');
    const hasDeleteTransactionPermission = permissions.includes('Delete Transaction');


    const fetchTableData = async () => {
        setData([]);
        setLoading(true);
        try {
            const response = await fetchData('ReceiptApi', `/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
            console.log(response);
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
        finally {
            setLoading(false); // Hide loading screen
        };

    };


    const handleDelete = async () => {
        deleteData('ReceiptApi', `${deleteItemId}`, token)
            .then((response) => {
                //fetchtableData(); // Refresh data after deletion
                fetchTableData();
                setDeleteItemId(null); // Reset the item ID
                setConfirmDialogOpen(false); // Close the dialog
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });

    };

    const handleActivate = (itemId) => {
        activateData('ReceiptApi', `${itemId}`, token)
            .then((response) => {
                //fetchtableData(); // Refresh data after deletion
                fetchTableData();
            })
            .catch((error) => {
                // Handle errors for the activation request
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const handleCreateNewClick = (page) => {
        history.push(`/${page}`);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handlePrint = async (saleid) => {
        try {

            const response = await fetchData('ReceiptApi', `/ReceiptPrint/${saleid}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();
            // iframe.contentWindow.print();
            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };


    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };


    const filteredDataItem = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );


    return (
        <div className="w-full max-w-sm container mt-6 mx-auto">

            <div className='w-full mb-2 flex flex-wrap items-center'>
                <h6 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>
                    Rent Receipt
                </h6>
                {loading && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}>
                        <div>
                            <CircularProgress style={{ color: 'white' }} />
                            <span style={{ color: 'white', marginTop: '10px', display: 'block' }}>Loading...</span>
                        </div>
                    </div>
                )}
                <div style={{ marginTop: '20px', gap: '10px' }}>
                    <input
                        className="shadow text-gray-700 leading-tight"
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? '300px' : '200px',
                            height: '40px',
                            border: '1px solid #ccc',
                            borderRadius: '15px',
                            padding: '0 20px',
                            transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                            outline: 'none',
                            borderColor: isFocused ? 'darkcyan' : '#ccc',
                            boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                            marginTop: '-5px',

                        }}
                    />
                </div>
                <div className='flex flex-col md:flex-row items-center mb-2 md:mb-0' style={{ marginLeft: '5px' }}>
                    <div className='flex flex-col md:mr-2 max-w-full md:max-w-xs'>
                        <label className="block uppercase tracking-wide text-black text-xs font-bold mb-1" htmlFor="fromDate">
                            From Date
                        </label>
                        <input
                            className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                        />
                    </div>
                    <div className='flex flex-col md:ml-2 max-w-full md:max-w-xs'>
                        <label className="block uppercase tracking-wide text-black text-xs font-bold mb-1" htmlFor="toDate">
                            To Date
                        </label>
                        <input
                            className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                        />
                    </div>
                </div>
                <Button
                    style={{
                        marginTop: '20px',
                        background: 'linear-gradient(to right, darkgreen, slategray)',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        color: 'white',
                        fontWeight: 'bolder',
                        textTransform: 'none',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                        maxHeight: '35px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '100px'
                    }}
                    onClick={fetchTableData}
                    className="linkcolor"
                >

                    {/* <RefreshIcon style={{ color: 'white' }} /> */}

                    <span style={{ color: 'white', fontWeight: 'bolder', marginLeft: '5px' }}>Refresh</span>
                </Button>

                {hasCreateTransactionPermission && (
                    <Tooltip title="Create Account Head">
                        <Link to="/ReceiptCreate" className="inline-block">
                            <Button
                                style={{
                                    marginTop: '20px',
                                    background: 'linear-gradient(to right, darkblue, slategray)',
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    color: 'white',
                                    fontWeight: 'bolder',
                                    textTransform: 'none',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                                    maxHeight: '35px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    maxWidth: '100px',
                                    marginLeft: '5px'
                                }}
                            >
                                {/* <AddIcon style={{ fontWeight: 'bolder' }} /> Create */}
                                Create
                            </Button>
                        </Link>
                    </Tooltip>
                )}
            </div>

            <div className='w-full mb-5 flex' style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    {/* <Table> */}
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#f3f3f3' }}> {/* Set background color */}
                            <TableRow>
                                <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd' }}>Bill Date</TableCell>
                                <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd' }}>Bill No</TableCell>
                                <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd' }}>Customer</TableCell>
                                <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd' }}>Room No</TableCell>
                                <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'right' }}>Amount</TableCell>
                                <TableCell sx={{ height: '30px', padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredDataItem.map((item, index) => (
                                <TableRow
                                    key={item.Id}
                                    className={item.IsActive ? 'activeRow' : 'inactiveRow'}
                                    sx={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#e0e0e0' }} // Alternating light colors
                                >
                                    <TableCell sx={{ padding: '2px', width: '10%' }}>{formatDate(item.BillDate)}</TableCell>
                                    <TableCell sx={{ padding: '2px', width: '10%' }}>{item.BillNo}</TableCell>
                                    <TableCell sx={{ padding: '2px', width: '20%' }}>{item.CustomerName}</TableCell>
                                    <TableCell sx={{ padding: '2px', width: '20%' }}>{item.RoomName}</TableCell>
                                    <TableCell sx={{ padding: '2px', textAlign: 'right', width: '10%' }}>{item.Amount.toFixed(2)}</TableCell>
                                    <TableCell sx={{ padding: '2px', width: '10%', marginLeft: '10px' }}>
                                        {hasEditTransactionPermission && item.IsActive && (
                                            <Link to={`/ReceiptEdit/${item.Id}`}>
                                                <IconButton>
                                                    <EditIcon style={{ color: 'red' }} />
                                                </IconButton>
                                            </Link>
                                        )}

                                        {hasDeleteTransactionPermission && (
                                            <IconButton onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}>
                                                {item.IsActive ? <DeleteIcon style={{ color: 'brown' }} /> : <ActivateIcon />}
                                            </IconButton>
                                        )}

                                        <IconButton onClick={() => handlePrint(item.Id)}>
                                            <PrintIcon style={{ color: 'teal' }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>

                        {/* Delete confirmation dialog */}
                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete this record?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button
                                    onClick={closeConfirmDialog}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: '#ffffff',
                                        border: '1px solid #dc3545',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        marginRight: '8px',
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    style={{
                                        backgroundColor: '#28a745',
                                        color: '#ffffff',
                                        border: '1px solid #28a745',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Confirm
                                </button>
                            </DialogActions>

                        </Dialog>
                    </Table>
                </TableContainer>
            </div>
        </div>

    );
}

export default ReceiptIndex;

